import { Controller, useFormContext, useWatch } from "react-hook-form";
import React from "react";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash-es";
import RemoteCollectorSetting from "./RemoteCollectorSetting/RemoteCollectorSetting";
import { CheckBox } from "../../../../../design-system/controls";
import { CheckBoxVariant } from "../../../../../design-system/controls/checkBox/CheckBox";
import {
  SkeletonLoader,
  Scheduler,
  DEFAULT_SCHEDULER_DATA,
  SCHEDULE_TYPES,
} from "../../../../../design-system/components";
import type { SchedulerData } from "../../../../../design-system/components/Scheduler/SchedulerUtils";
import "./CollectionSettings.scss";
import PeriodicSettings from "./PeriodicSettings";
import { REQUIRED_ERROR_MESSAGE } from "../../../../../utils";

interface CollectionSettingsProps {
  scheduledEnabled?: boolean;
  scheduleSettings?: SchedulerData;
  disable?: boolean;
  isRapable?: boolean;
  rapId?: number | string;
  shouldEnableLastCollected?: boolean;
  shouldEnableLastReported?: boolean;
  maxDaysNotSeenToCloseSetting?: number;
  loadingSlowInfo?: boolean;
  lastCollectedThreshold?: number;
  isNew?: boolean;
  shouldShowPeriodicFields?: boolean;
  shouldCheckLastCollected?: boolean;
  shouldCheckLastReported?: boolean;
}

const CollectionSettings: React.FC<CollectionSettingsProps> = ({
  scheduledEnabled,
  scheduleSettings,
  disable,
  isRapable,
  rapId,
  shouldEnableLastCollected,
  shouldEnableLastReported,
  maxDaysNotSeenToCloseSetting,
  loadingSlowInfo,
  lastCollectedThreshold,
  isNew,
  shouldShowPeriodicFields = false,
  shouldCheckLastCollected,
  shouldCheckLastReported,
}) => {
  const { control } = useFormContext();

  const { t } = useTranslation();
  const enableScheduling = useWatch({
    control,
    name: `enableScheduling`,
    defaultValue:
      typeof scheduledEnabled === "boolean" ? scheduledEnabled : true,
  });

  return (
    <>
      <h4 className={"collection-seetings-title"}>
        {t("Collection Settings")}
      </h4>
      {!!isRapable && (
        <RemoteCollectorSetting
          rapId={rapId}
          disable={disable}
        ></RemoteCollectorSetting>
      )}
      {!loadingSlowInfo && (
        <Controller
          name={`enableScheduling`}
          defaultValue={
            typeof scheduledEnabled === "boolean" ? scheduledEnabled : true
          }
          render={({ field: { value, onChange } }) => {
            return (
              <CheckBox
                data-testid={"enable-scheduling"}
                className={"enable-scheduling"}
                variant={CheckBoxVariant.TOGGLE}
                onChange={(val, newValue) => {
                  onChange(newValue);
                }}
                checked={value}
                label={t("Enabled")}
                disabled={disable}
              />
            );
          }}
          control={control}
        />
      )}
      {!!loadingSlowInfo && (
        <SkeletonLoader wrapperClassName="card-loader-img" width={"40%"} />
      )}
      {!loadingSlowInfo &&
        ((!isNew && !isEmpty(scheduleSettings)) || isNew) &&
        enableScheduling && (
          <>
            <div className="datasource-instance-details-scheduler-wrapper">
              <Controller
                shouldUnregister
                name={`schedule`}
                defaultValue={
                  !isEmpty(scheduleSettings)
                    ? scheduleSettings
                    : DEFAULT_SCHEDULER_DATA
                }
                render={({ field: { value, onChange } }) => {
                  return (
                    <Scheduler
                      allowedTypes={[SCHEDULE_TYPES.DAY, SCHEDULE_TYPES.WEEK]}
                      onChange={onChange}
                      data={value}
                      hideInterval
                      hideEnds
                      required
                      disabled={disable}
                    />
                  );
                }}
                control={control}
                rules={{ required: REQUIRED_ERROR_MESSAGE }}
              />
            </div>
            {!!shouldShowPeriodicFields && (
              <PeriodicSettings
                shouldEnableLastCollected={shouldEnableLastCollected}
                shouldEnableLastReported={shouldEnableLastReported}
                maxDaysNotSeenToCloseSetting={maxDaysNotSeenToCloseSetting}
                lastCollectedThreshold={lastCollectedThreshold}
                shouldCheckLastCollected={shouldCheckLastCollected}
                shouldCheckLastReported={shouldCheckLastReported}
                disabled={disable}
              />
            )}
          </>
        )}
    </>
  );
};
export default CollectionSettings;
